import { IconName } from '@infotech-soe/erp-ui-kit';
import {
  CategoryName,
  IMainMenuActionItem,
  IMainMenuCategoryItem,
  IMainMenuGroupItem,
  IMainMenuItem,
  IMainMenuSeparatorItem,
} from '@infotech-soe/metaschema';

import IIntlService from '../intl/IIntlService';
import IntlService from '../intl/IntlService';
import IUUIDService from '../uid/IUUIDService';
import UniqueId from '../uid/UniqueId';
import UUIDService from '../uid/UUIDService';

import MenuItemViewModel, {
  ActionMenuItemViewModel,
  DisplayModeMenuItemViewModel,
  GroupMenuItemViewModel,
  MenuItemViewModelType,
  SeparatorMenuItemViewModel,
} from './MenuItemViewModel';
import MenuViewModel from './MenuViewModel';

/**
 * Builds menu view model from menu schema.
 */
class MenuViewModelBuilder {
  private allItems: Record<UniqueId, MenuItemViewModel> = {};
  private readonly rootItems: UniqueId[] = [];
  private readonly intlService: IIntlService;
  private readonly idService: IUUIDService;

  constructor(
    private readonly application: string,
    private readonly allowedCategories: CategoryName[]
  ) {
    this.idService = new UUIDService();
    this.intlService = new IntlService();
  }

  /**
   * Adds a root menu item.
   *
   * @param item - Menu item schema.
   */
  public async addRootItem(item: IMainMenuItem): Promise<void> {
    await this.addItem(0, item, this.rootItems);

    if (this.rootItems.length > 0) {
      const lastItemId = this.rootItems[this.rootItems.length - 1];
      const lastItem = this.allItems[lastItemId];

      if (lastItem.type === MenuItemViewModelType.SEPARATOR) {
        this.rootItems.pop();
        delete this.allItems[lastItemId];
      }
    }
  }

  /**
   * Builds final view model structure.
   */
  public build(): MenuViewModel {
    return {
      application: this.application,
      items: this.allItems,
    };
  }

  private async addItem(
    menuNestinglevel: number,
    item: IMainMenuItem,
    currentList: UniqueId[],
    parent?: UniqueId
  ): Promise<void> {
    let prevItem: MenuItemViewModel | undefined;

    if (currentList.length > 0) {
      prevItem = this.allItems[currentList[currentList.length - 1]];
    }

    let newItemId: UniqueId | null = null;

    switch (item.kind) {
      case MenuItemViewModelType.GROUP:
        newItemId = await this.addGroupItem(menuNestinglevel, item, parent);
        break;
      case MenuItemViewModelType.CATEGORY:
        newItemId = await this.addCategoryItem(menuNestinglevel, item, parent);
        break;
      case MenuItemViewModelType.SEPARATOR:
        newItemId = await this.addSeparatorItem(
          menuNestinglevel,
          item,
          prevItem
        );
        break;
      case MenuItemViewModelType.ACTION:
        newItemId = await this.addActionItem(menuNestinglevel, item, parent);
        break;
      case 'url':
        throw new Error('Not implemented');
    }

    if (newItemId) {
      currentList.push(newItemId);
    }
  }

  private async addGroupItem(
    menuNestinglevel: number,
    item: IMainMenuGroupItem,
    parent?: UniqueId
  ): Promise<UniqueId | null> {
    const children: UniqueId[] = [];
    const id = this.idService.generateId();

    for (const childItem of item.items) {
      await this.addItem(
        parent ? menuNestinglevel + 1 : 0,
        childItem,
        children,
        id
      );
    }

    const viewModel: GroupMenuItemViewModel = {
      type: MenuItemViewModelType.GROUP,
      id,
      label: await this.intlService.getApplicationMenuTitle(item.name),
      icon: item.icon as IconName,
      children,
      parent,
      menuNestinglevel: parent ? menuNestinglevel + 1 : 0,
    };

    if (viewModel.children.length === 0) {
      return null;
    } else if (
      this.allItems[viewModel.children[viewModel.children.length - 1]].type ===
      MenuItemViewModelType.SEPARATOR
    ) {
      delete this.allItems[viewModel.children[viewModel.children.length - 1]];
      viewModel.children.pop();
    }

    this.allItems[viewModel.id] = viewModel;

    return viewModel.id;
  }

  private async addSeparatorItem(
    menuNestinglevel: number,
    _item: IMainMenuSeparatorItem,
    prevItem?: MenuItemViewModel
  ): Promise<UniqueId | null> {
    const viewModel: SeparatorMenuItemViewModel = {
      type: MenuItemViewModelType.SEPARATOR,
      id: this.idService.generateId(),
      menuNestinglevel: parent ? menuNestinglevel + 1 : 0,
    };

    if (!prevItem || prevItem.type === MenuItemViewModelType.SEPARATOR) {
      return null;
    }

    this.allItems[viewModel.id] = viewModel;

    return viewModel.id;
  }

  private async addCategoryItem(
    menuNestinglevel: number,
    item: IMainMenuCategoryItem,
    parent?: UniqueId
  ): Promise<UniqueId | null> {
    if (
      this.allowedCategories &&
      !this.allowedCategories.includes(item.permissionCategory ?? item.category)
    ) {
      return null;
    }

    const viewModel: DisplayModeMenuItemViewModel = {
      type: MenuItemViewModelType.DISPLAY_MODE,
      id: this.idService.generateId(),
      category: item.category,
      parent,
      displayMode: item.displayMode || 'main',
      inParams: item.inParams,
      icon: item.icon as IconName,
      label: await this.intlService.getApplicationMenuTitle(item.name),
      menuNestinglevel: parent ? menuNestinglevel + 1 : 0,
    };

    this.allItems[viewModel.id] = viewModel;

    return viewModel.id;
  }

  private async addActionItem(
    menuNestinglevel: number,
    item: IMainMenuActionItem,
    parent?: UniqueId
  ): Promise<UniqueId | null> {
    if (
      this.allowedCategories &&
      !this.allowedCategories.includes(item.category)
    ) {
      return null;
    }

    const viewModel: ActionMenuItemViewModel = {
      type: MenuItemViewModelType.ACTION,
      id: this.idService.generateId(),
      category: item.category,
      displayMode: item.displayMode,
      inParams: item.inParams,
      action: item.action,
      icon: item.icon as IconName,
      label: await this.intlService.getApplicationMenuTitle(item.name),
      menuNestinglevel: parent ? menuNestinglevel + 1 : 0,
    };

    this.allItems[viewModel.id] = viewModel;

    return viewModel.id;
  }
}

export default MenuViewModelBuilder;
